import { useEffect, useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import {
	dashboardClearBusinesses,
	dashboardClearCursors
} from '@/modules/dashboard/dashboardActions';
import './SearchTab.scss';
import type { BusinessQuery } from '@/modules/dashboard/dashboardTypes';
import { searchGetSearchesRequest } from '@/modules/search/searchActions';
import { sendAnalytics } from '@/services/analyticsService';
import SearchTabSearch from './SearchTabSearch/SearchTabSearch';
import { useSearchTabContext } from './SearchTabContext';
import ClearFiltersButton from '../ClearFiltersButton/ClearFiltersButton';
import SearchTabBusinessInfo from './SearchTabBusinessInfo/SearchTabBusinessInfo';
import SearchTabContactInfo from './SearchTabContactInfo/SearchTabContactInfo';
import SearchTabContactStatus from './SearchTabContactStatus/SearchTabContactStatus';
import SearchTabLocation from './SearchTabLocation/SearchTabLocation';
import { SearchTabAiSearch } from './SearchTabAiSearch';

interface SearchTabProps {
	handleSwitchView?: (view: 'businesses' | 'map') => void;
	onFilterChange: (businessQuery: BusinessQuery) => void;
	onGetBusiness: (businessQuery: BusinessQuery) => void;
}

const SearchTab = ({
	handleSwitchView,
	onFilterChange,
	onGetBusiness
}: SearchTabProps) => {
	const [query, user] = useSelector(
		(state: RootState) => [
			state.dashboard.get('query').toJS(),
			state.user.getIn(['authorize', 'user']).toJS()
		],
		shallowEqual
	);

	const dispatch = useDispatch();

	const getSearches = () => dispatch(searchGetSearchesRequest());

	const {
		setSelectedLocation,
		setYelpReviewsFilterMax,
		setYelpReviewsFilterMin
	} = useSearchTabContext();

	const defaultQuery = {
		minDistance: 0,
		maxDistance: 40,
		limit: 50,
		skip: 0,
		sortField: 'score',
		sort: { score: -1 },
		count: true
	};

	const handleResetAllFilters = () => {
		onFilterChange(defaultQuery);

		setSelectedLocation('');
		setYelpReviewsFilterMin('');
		setYelpReviewsFilterMax('');
		dispatch(dashboardClearBusinesses([])); // Clears the businesses list
		dispatch(dashboardClearCursors()); // Clears the cursors

		sendAnalytics({
			type: 'track',
			userId: user.email,
			properties: {
				query
			},
			eventName: 'User cleared all filters'
		});
	};

	const filtersCount = useMemo(() => {
		let count = 0;

		if (query?.contactedYes || query?.contactedNo) {
			count++;
		}

		if (query?.scheduledYes || query?.scheduledNo) {
			count++;
		}

		if (query?.lead && query?.lead !== undefined) {
			count++;
		}

		if (query?.includeEmailsYes) {
			count++;
		}

		if (query?.includeFBMessenger) {
			count++;
		}

		if (query?.includeIGMessenger) {
			count++;
		}

		if (query?.includePhoneNumber) {
			count++;
		}

		if (query?.includeWebsitesYes) {
			count++;
		}

		if (query?.includeWebsitesNo) {
			count++;
		}

		if (query?.chains || query?.nonChains) {
			count++;
		}

		if (query?.countOne) {
			count++;
		}

		if (query?.countTwoTen) {
			count++;
		}

		if (query?.countTenPlus) {
			count++;
		}

		if (query?.includePOIs && query?.includePOIs !== undefined) {
			count++;
		}

		if (query?.excludePOIs && query?.excludePOIs !== undefined) {
			count++;
		}

		if (
			query?.coordinates ||
			(query.country && query.state) ||
			query.country
		) {
			count++;
		}

		if (query?.salesVolumeLessThan5M) {
			count++;
		}

		if (query?.salesVolume5To10M) {
			count++;
		}

		if (query?.salesVolume10To20M) {
			count++;
		}

		if (query?.salesVolume20To50M) {
			count++;
		}

		if (query?.salesVolume50To100M) {
			count++;
		}

		if (query?.salesVolumeOver100M) {
			count++;
		}

		if (query?.employees1To49) {
			count++;
		}

		if (query?.employees50To99) {
			count++;
		}

		if (query?.employees100To249) {
			count++;
		}

		if (query?.employees250To499) {
			count++;
		}

		if (query?.employeesOver500) {
			count++;
		}

		if (query?.sqftSmall) {
			count++;
		}

		if (query?.sqftMedium) {
			count++;
		}

		if (query?.sqftLarge) {
			count++;
		}

		if (query?.searches?.length > 0) {
			count += query.searches.length;
		}

		// Yelp Filters
		if (
			(query?.minYelpReviews &&
				query?.minYelpReviews !== undefined &&
				query?.minYelpReviews !== null) ||
			(query?.maxYelpReviews &&
				query?.maxYelpReviews !== undefined &&
				query?.maxYelpReviews !== null)
		) {
			count++;
		}

		return count;
	}, [
		query?.chains,
		query?.contactedNo,
		query?.contactedYes,
		query?.coordinates,
		query?.countOne,
		query?.countTenPlus,
		query?.countTwoTen,
		query?.country,
		query?.employees100To249,
		query?.employees1To49,
		query?.employees250To499,
		query?.employees50To99,
		query?.employeesOver500,
		query?.excludePOIs,
		query?.includeEmailsYes,
		query?.includeFBMessenger,
		query?.includeIGMessenger,
		query?.includePOIs,
		query?.includePhoneNumber,
		query?.includeWebsitesNo,
		query?.includeWebsitesYes,
		query?.lead,
		query?.maxYelpReviews,
		query?.minYelpReviews,
		query?.nonChains,
		query?.salesVolume10To20M,
		query?.salesVolume20To50M,
		query?.salesVolume50To100M,
		query?.salesVolume5To10M,
		query?.salesVolumeLessThan5M,
		query?.salesVolumeOver100M,
		query?.scheduledNo,
		query?.scheduledYes,
		query?.searches?.length,
		query?.sqftLarge,
		query?.sqftMedium,
		query?.sqftSmall,
		query?.state
	]);

	useEffect(() => {
		getSearches();
		setSelectedLocation(query.selectedLocation || '');

		if (filtersCount === 0) {
			handleResetAllFilters();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="relative h-full w-full overflow-auto">
			<span className="inline-flex h-14 w-full items-center gap-2.5 border-b border-dm-mid-grey px-4 text-lg text-dm-white">
				Filters
				{filtersCount > 0 && (
					<ClearFiltersButton
						filterCount={filtersCount}
						onClick={handleResetAllFilters}
						large
					/>
				)}
			</span>

			<section className="absolute flex h-auto flex-col">
				<SearchTabAiSearch
					onFilterChange={onFilterChange}
					filterCount={filtersCount}
				/>
				<SearchTabSearch
					onFilterChange={onFilterChange}
					onGetBusiness={onGetBusiness}
				/>
				<SearchTabLocation
					handleSwitchView={handleSwitchView}
					onFilterChange={onFilterChange}
					onGetBusiness={onGetBusiness}
				/>
				<SearchTabContactStatus
					onFilterChange={onFilterChange}
					onGetBusiness={onGetBusiness}
				/>
				<SearchTabContactInfo
					onFilterChange={onFilterChange}
					onGetBusiness={onGetBusiness}
				/>
				<SearchTabBusinessInfo
					onFilterChange={onFilterChange}
					onGetBusiness={onGetBusiness}
				/>
			</section>
		</div>
	);
};

export default SearchTab;
